import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import toast from "react-hot-toast";
import { X, Users, Plus, Trash2, Search } from "lucide-react";
import {
  getClass,
  addStudentToClass,
  removeStudentFromClass,
} from "../../services/classes";
import { getStudents } from "../../services/students";
import { getCourses } from "../../services/courses";

interface StudentEnrollmentModalProps {
  isOpen: boolean;
  onClose: () => void;
  classId: string;
}

const StudentEnrollmentModal: React.FC<StudentEnrollmentModalProps> = ({
  isOpen,
  onClose,
  classId,
}) => {
  const queryClient = useQueryClient();
  const [searchTerm, setSearchTerm] = useState("");
  const [gradeFilter, setGradeFilter] = useState("all");
  const [courseFilter, setCourseFilter] = useState<string>("all");

  // Fetch class data
  const { data: classResponse, isLoading: classLoading } = useQuery({
    queryKey: ["class", classId],
    queryFn: () => getClass(classId),
    enabled: isOpen && !!classId,
  });

  // Fetch list of courses for the course filter select
  const { data: coursesResponse } = useQuery({
    queryKey: ["courses", "all"],
    queryFn: () => getCourses({ limit: 100 }),
    enabled: isOpen,
  });

  // Fetch available students
  const { data: studentsResponse, isLoading: studentsLoading } = useQuery({
    queryKey: ["students", searchTerm, gradeFilter, courseFilter],
    queryFn: () =>
      getStudents({
        search: searchTerm,
        classId: undefined,
        courseId: courseFilter !== "all" ? courseFilter : undefined,
        limit: 50,
      }),
    enabled: isOpen,
  });

  const classData = classResponse?.class;
  const availableStudents = studentsResponse?.data || [];
  const enrolledStudents = classData?.students || []; // Array of objects with studentId, enrollmentDate, status
  const enrolledStudentIds = enrolledStudents.map(
    (enrollment: any) => enrollment.studentId || enrollment._id
  );

  // Add student mutation
  const addStudentMutation = useMutation({
    mutationFn: ({ studentId }: { studentId: string }) =>
      addStudentToClass(classId, { studentId }),
    onSuccess: () => {
      toast.success("Student enrolled successfully");
      queryClient.invalidateQueries({ queryKey: ["class", classId] });
      queryClient.invalidateQueries({ queryKey: ["classes"] });
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.message || "Failed to enroll student");
    },
  });

  // Remove student mutation
  const removeStudentMutation = useMutation({
    mutationFn: (studentId: string) =>
      removeStudentFromClass(classId, studentId),
    onSuccess: () => {
      toast.success("Student removed successfully");
      queryClient.invalidateQueries({ queryKey: ["class", classId] });
      queryClient.invalidateQueries({ queryKey: ["classes"] });
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.message || "Failed to remove student");
    },
  });

  if (!isOpen) return null;

  const handleAddStudent = (studentId: string) => {
    addStudentMutation.mutate({ studentId });
  };

  const handleRemoveStudent = (studentId: string) => {
    if (
      window.confirm(
        "Are you sure you want to remove this student from the class?"
      )
    ) {
      removeStudentMutation.mutate(studentId);
    }
  };

  const unenrolledStudents = availableStudents.filter(
    (student) => !enrolledStudentIds.includes(student._id)
  );

  return (
    <div className="fixed inset-0 z-[9999] overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        <div
          className="fixed inset-0 bg-black/50 transition-opacity z-[9998]"
          onClick={onClose}
        ></div>

        <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-4xl sm:w-full relative z-[10000]">
          {/* Header */}
          <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-blue-100 sm:mx-0 sm:h-10 sm:w-10">
                  <Users className="h-6 w-6 text-blue-600" />
                </div>
                <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                  <h3 className="text-lg leading-6 font-medium text-gray-900">
                    Manage Class Enrollment
                  </h3>
                  <p className="text-sm text-gray-500">
                    {classData?.name} - {classData?.grade}
                  </p>
                </div>
              </div>
              <button
                onClick={onClose}
                className="bg-white rounded-md text-gray-400 hover:text-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                <X className="h-6 w-6" />
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="bg-white px-4 pb-4 sm:p-6">
            {classLoading ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              </div>
            ) : (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Enrolled Students */}
                <div>
                  <h4 className="text-lg font-medium text-gray-900 mb-4">
                    Enrolled Students ({enrolledStudents.length}/
                    {classData?.capacity})
                  </h4>
                  <div className="border rounded-lg max-h-96 overflow-y-auto">
                    {enrolledStudents.length > 0 ? (
                      <div className="divide-y divide-gray-200">
                        {enrolledStudents.map((enrollment: any) => {
                          const studentId =
                            enrollment.studentId || enrollment._id;
                          const student = availableStudents.find(
                            (s) => s._id === studentId
                          );
                          return (
                            <div
                              key={studentId}
                              className="p-3 flex items-center justify-between hover:bg-gray-50"
                            >
                              <div>
                                <p className="text-sm font-medium text-gray-900">
                                  {student?.userInfo?.firstName}{" "}
                                  {student?.userInfo?.lastName}
                                </p>
                                <p className="text-sm text-gray-500">
                                  {student?.userInfo?.email || studentId}
                                </p>
                                <p className="text-xs text-gray-400">
                                  Enrolled:{" "}
                                  {new Date(
                                    enrollment.enrollmentDate
                                  ).toLocaleDateString()}
                                </p>
                              </div>
                              <button
                                onClick={() => handleRemoveStudent(studentId)}
                                disabled={removeStudentMutation.isPending}
                                className="text-red-600 hover:text-red-800 p-1 rounded"
                                title="Remove Student"
                              >
                                <Trash2 className="h-4 w-4" />
                              </button>
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <div className="p-6 text-center text-gray-500">
                        No students enrolled yet
                      </div>
                    )}
                  </div>
                </div>

                {/* Available Students */}
                <div>
                  <h4 className="text-lg font-medium text-gray-900 mb-4">
                    Available Students
                  </h4>

                  {/* Filters */}
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-4">
                    {/* Search */}
                    <div className="relative">
                      <input
                        type="text"
                        placeholder="Search students..."
                        className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                      />
                    </div>

                    {/* Course Filter */}
                    <select
                      value={courseFilter}
                      onChange={(e) => setCourseFilter(e.target.value)}
                      className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      <option value="all">All Courses</option>
                      {coursesResponse?.data &&
                      Array.isArray(coursesResponse.data)
                        ? coursesResponse.data.map((c: any) => (
                            <option key={c._id} value={c._id}>
                              {c.name}
                            </option>
                          ))
                        : null}
                    </select>

                    {/* Reset Filters */}
                    <button
                      onClick={() => {
                        setSearchTerm("");
                        setCourseFilter("all");
                      }}
                      className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2"
                    >
                      Reset Filters
                    </button>
                  </div>

                  <div className="border rounded-lg max-h-96 overflow-y-auto">
                    {studentsLoading ? (
                      <div className="p-6 text-center">
                        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600 mx-auto"></div>
                      </div>
                    ) : unenrolledStudents.length > 0 ? (
                      <div className="divide-y divide-gray-200">
                        {unenrolledStudents.map((student) => (
                          <div
                            key={student._id}
                            className="p-3 flex items-center justify-between hover:bg-gray-50"
                          >
                            <div>
                              <p className="text-sm font-medium text-gray-900">
                                {student.userInfo?.firstName}{" "}
                                {student.userInfo?.lastName}
                              </p>
                              <p className="text-sm text-gray-500">
                                {student.userInfo?.email}
                              </p>
                            </div>
                            <button
                              onClick={() => handleAddStudent(student._id)}
                              disabled={
                                addStudentMutation.isPending ||
                                enrolledStudentIds.length >=
                                  (classData?.capacity || 0)
                              }
                              className="text-green-600 hover:text-green-800 p-1 rounded disabled:opacity-50 disabled:cursor-not-allowed"
                              title="Add Student"
                            >
                              <Plus className="h-4 w-4" />
                            </button>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="p-6 text-center text-gray-500">
                        No available students found
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
            <button
              onClick={onClose}
              className="w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:ml-3 sm:w-auto sm:text-sm"
            >
              Done
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StudentEnrollmentModal;
